import { Controller } from "@hotwired/stimulus"
import {
  openDisclosures,
  openRows,
  setOpenDisclosures,
  setOpenRows,
  toggleIn,
} from "../lib/plan_row_state"

// One round row: whether it is expanded, and which of its disclosures are open.
//
// This state lives on the *row* rather than on the list around it because the
// row is what gets replaced. Editing a reminder used to re-render the whole
// planner — ten rows destroyed and rebuilt for a one-row change, which reset the
// drawer's scroll and made every row visibly collapse and pop back open as the
// list's `connect()` restored them. Now a non-date edit replaces just this row,
// and a row that restores *itself* on connect comes back already open, with no
// flash and nothing else on the page touched.
//
export default class extends Controller {
  static targets = ["panel"]
  static values = { roundId: String, seasonId: String }

  connect() {
    if (openRows(this.seasonIdValue).includes(this.roundIdValue))
      this.#show(true)

    const open = openDisclosures(this.seasonIdValue)
    this.#disclosures.forEach((details) => {
      if (open.includes(this.#disclosureKey(details))) details.open = true
    })
  }

  toggleRow() {
    this.#show(this.element.dataset.open !== "true")
  }

  // Fired by the <details> element's own `toggle` event, so it reports the state
  // the browser has already applied rather than one we have to guess at.
  toggleMore(event) {
    const details = event.currentTarget
    setOpenDisclosures(
      this.seasonIdValue,
      toggleIn(
        openDisclosures(this.seasonIdValue),
        this.#disclosureKey(details),
        details.open,
      ),
    )
  }

  // Called by the list when a drag starts: an open panel is 200px of the list,
  // and "drop it two rows down" stops being a judgement anyone can make when one
  // of those rows is six times the height of the others.
  close() {
    this.#show(false)
  }

  #show(open) {
    if (this.hasPanelTarget) this.panelTarget.classList.toggle("hidden", !open)
    // Mirrored onto the row so the header can stay lit while its fields are
    // open: "which round am I editing" should be answerable without reading
    // the panel.
    this.element.dataset.open = String(open)
    setOpenRows(
      this.seasonIdValue,
      toggleIn(openRows(this.seasonIdValue), this.roundIdValue, open),
    )
  }

  get #disclosures() {
    return [...this.element.querySelectorAll("details[data-disclosure-key]")]
  }

  #disclosureKey(details) {
    return `${this.roundIdValue}:${details.dataset.disclosureKey}`
  }
}
